import { asPartialDef } from "../coerce";
import { matchesAny, type NormalizeOpts } from "../normalize";
import type { BlockPlugin, GradeResult, ValidationResult } from "../types";

export interface Blank {
  id: string;
  /** Respuestas aceptadas (cualquiera cuenta). */
  answers: string[];
  normalize?: NormalizeOpts;
}

export interface FillBlankDef {
  /** Texto con marcadores, p. ej. "El {{0}} es azul". Solo informativo. */
  text: string;
  blanks: Blank[];
}

export interface FillBlankResponse {
  /** blankId → texto escrito. */
  filled: Record<string, string>;
}

export const fillBlankBlock: BlockPlugin<FillBlankDef, FillBlankResponse> = {
  type: "fill-blank",
  interaction: "text",
  autoGradable: true,

  validate(def: unknown): ValidationResult<FillBlankDef> {
    const d = asPartialDef<FillBlankDef>(def);
    const errors: string[] = [];
    if (!d) {
      return { ok: false, errors: ["fill-blank: definición no es un objeto"] };
    }
    if (typeof d.text !== "string") {
      errors.push("fill-blank: `text` requerido");
    }
    if (!Array.isArray(d.blanks) || d.blanks.length < 1) {
      errors.push("fill-blank: al menos 1 `blanks`");
    } else {
      for (const b of d.blanks) {
        if (!b || typeof b.id !== "string" || !Array.isArray(b.answers) || b.answers.length < 1) {
          errors.push("fill-blank: cada hueco requiere `id` y `answers` no vacío");
          break;
        }
      }
    }
    if (errors.length > 0) {
      return { ok: false, errors };
    }
    return { ok: true, def: { text: d.text ?? "", blanks: d.blanks ?? [] } };
  },

  grade(def: FillBlankDef, response: FillBlankResponse | undefined): GradeResult {
    const filled = response?.filled ?? {};
    const revealed: Record<string, string> = {};
    let hits = 0;
    for (const blank of def.blanks) {
      revealed[blank.id] = blank.answers[0] ?? "";
      const given = filled[blank.id] ?? "";
      if (matchesAny(given, blank.answers, blank.normalize)) {
        hits += 1;
      }
    }
    const total = def.blanks.length;
    const score = total === 0 ? 0 : hits / total;
    return { correct: hits === total, score, maxScore: 1, revealed };
  },
};
